import React from 'react';

import {cx} from '../../util';

/**
 * Shared visual primitives for the Practice analytics report. All rubric scores
 * are on a 1–5 scale, so colour, width, and gauge math everywhere assume max=5.
 */

export const SCALE_MAX = 5;

/** Maps a 1–5 rubric average onto a performance colour band. */
export const scoreBand = (avg) => {
  if (avg == null) {
    return {
      label: 'No data', text: 'text-gray-400', bg: 'bg-gray-100', fill: '#d1d5db', solid: 'bg-gray-300',
    };
  }
  if (avg < 2.5) {
    return {
      label: 'Struggling', text: 'text-rose-700', bg: 'bg-rose-50', fill: '#e11d48', solid: 'bg-rose-500',
    };
  }
  if (avg < 3.25) {
    return {
      label: 'Developing', text: 'text-amber-700', bg: 'bg-amber-50', fill: '#f59e0b', solid: 'bg-amber-500',
    };
  }
  if (avg < 4.0) {
    return {
      label: 'Solid', text: 'text-lime-700', bg: 'bg-lime-50', fill: '#65a30d', solid: 'bg-lime-500',
    };
  }
  return {
    label: 'Strong', text: 'text-emerald-700', bg: 'bg-emerald-50', fill: '#059669', solid: 'bg-emerald-500',
  };
};

export const fmtScore = (avg) => (avg == null ? '—' : avg.toFixed(2));

export const pct = (avg, max = SCALE_MAX) => (
  avg == null ? 0 : Math.max(0, Math.min(100, (avg / max) * 100))
);

/** Small inline pill showing a numeric score with its performance colour. */
export const ScorePill = ({avg}) => {
  const band = scoreBand(avg);
  return (
    <span className={cx('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold', band.bg, band.text)}>
      {fmtScore(avg)}
    </span>
  );
};

/** Circular gauge (donut) for an overall average out of 5. */
export const Gauge = ({avg, size = 116, label}) => {
  const stroke = Math.max(6, Math.round(size * 0.085));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const band = scoreBand(avg);
  const dash = (pct(avg) / 100) * c;
  // Keep the number comfortably inside the ring so high scores don't crowd it.
  const numberClass = size >= 104 ? 'text-xl' : 'text-sm';

  return (
    <div className="relative inline-flex items-center justify-center" style={{width: size, height: size}}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={band.fill}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c - dash}`}
        />
      </svg>
      <div className="absolute flex flex-col items-center leading-none">
        <span className={cx('font-bold tabular-nums text-gray-900', numberClass)}>{fmtScore(avg)}</span>
        {label && <span className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-400">{label}</span>}
      </div>
    </div>
  );
};

/**
 * Horizontal bar chart of rubric attribute averages for a single interview
 * type. Bars are coloured by performance band so weak spots pop visually.
 */
export const AttributeBars = ({attributes}) => (
  <div className="flex flex-col gap-3">
    {attributes.map((attr) => {
      const band = scoreBand(attr.avg);
      return (
        <div key={attr.key} className="grid grid-cols-[140px_1fr_44px] items-center gap-3">
          <div className="truncate text-sm font-medium text-gray-700" title={attr.label}>{attr.label}</div>
          <div className="h-6 overflow-hidden rounded-md bg-gray-100">
            <div
              className={cx('flex h-full items-center justify-end rounded-md pr-2 transition-all', band.solid)}
              style={{width: `${pct(attr.avg)}%`}}
            />
          </div>
          <div className={cx('text-right text-sm font-semibold tabular-nums', band.text)}>{fmtScore(attr.avg)}</div>
        </div>
      );
    })}
    {attributes.length === 0 && <div className="text-sm text-gray-400">No rubric data in this range.</div>}
  </div>
);

/**
 * Grouped bars comparing one attribute across seniority levels
 * (Beginner / Intermediate / Advanced).
 */
export const LevelGroupedBars = ({rows, levels}) => {
  const levelColors = ['#a5b4fc', '#6366f1', '#3730a3'];
  return (
    <div className="flex flex-col gap-4">
      {rows.map((row) => (
        <div key={row.key}>
          <div className="mb-1 text-sm font-medium text-gray-700">{row.label}</div>
          <div className="flex flex-col gap-1.5">
            {levels.map((lvl, i) => {
              const cell = row.byLevel[lvl.level];
              return (
                <div key={lvl.level} className="grid grid-cols-[96px_1fr_44px] items-center gap-2">
                  <div className="text-xs text-gray-500">{lvl.label}</div>
                  <div className="h-4 overflow-hidden rounded bg-gray-100">
                    <div
                      className="h-full rounded transition-all"
                      style={{width: `${pct(cell?.avg)}%`, backgroundColor: levelColors[i] || '#6366f1'}}
                    />
                  </div>
                  <div className="text-right text-xs font-semibold tabular-nums text-gray-600">{fmtScore(cell?.avg)}</div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

/** Monthly trend line of overall average score. */
export const TrendLine = ({trend}) => {
  const width = 720;
  const height = 220;
  const padX = 36;
  const padY = 24;
  const points = trend.filter((t) => t.avg != null);

  if (points.length === 0) {
    return <div className="text-sm text-gray-400">Not enough data to plot a trend.</div>;
  }

  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const stepX = points.length > 1 ? innerW / (points.length - 1) : 0;
  const yFor = (avg) => padY + innerH - ((avg - 1) / (SCALE_MAX - 1)) * innerH;
  const xFor = (i) => padX + (points.length > 1 ? i * stepX : innerW / 2);

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yFor(p.avg)}`).join(' ');
  const areaPath = `${linePath} L ${xFor(points.length - 1)} ${padY + innerH} L ${xFor(0)} ${padY + innerH} Z`;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full">
      {[1, 2, 3, 4, 5].map((g) => (
        <g key={g}>
          <line x1={padX} x2={width - padX} y1={yFor(g)} y2={yFor(g)} stroke="#f1f5f9" strokeWidth="1" />
          <text x={8} y={yFor(g) + 4} className="fill-gray-400 text-[10px]">{g}</text>
        </g>
      ))}
      <path d={areaPath} fill="#6366f1" fillOpacity="0.08" />
      <path d={linePath} fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {points.map((p, i) => (
        <g key={p.month}>
          <circle cx={xFor(i)} cy={yFor(p.avg)} r="4" fill="#fff" stroke="#6366f1" strokeWidth="2" />
          {(points.length <= 12 || i % 2 === 0) && (
            <text x={xFor(i)} y={height - 6} textAnchor="middle" className="fill-gray-400 text-[10px]">
              {p.month.slice(2)}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
};
